import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { ToggleButton, Tooltip } from '@mui/material';

import {
  HeaderRow,
  TitleContainer,
  SubTitle,
  Title,
} from '../../../components/v2/Header';
import { COUNTS_RECOMPUTE_INTERVAL_MINUTES } from '../../../Model/CoachNotificationView';
import { formatRelativeTime } from '../../../utils/date';
import useQuickSearch from '../../../hooks/useQuickSearch';
/*
 * The toggle group itself, rather than DateFilter around it: this picks an SLA threshold in hours,
 * so `selectedPeriod` and the day-window labels DateFilter defaults to describe nothing here.
 */
import { StyledToggleButtonGroup } from '../../components/DateFilter/styles';
import LabelCheckbox from '../../components/LabelCheckbox';
import MultiSelectFilter from '../../components/MultiSelectFilter';

import ACLeadTable from './components/ACLeadTable';
import useACLeadData from './hooks/useACLeadData';
import useStoredFilter, {
  AC_FILTER_STORAGE_KEY,
  HIDE_SUSPECT_STORAGE_KEY,
  PAST_DUE_THRESHOLD_STORAGE_KEY,
} from './hooks/useStoredFilter';
import {
  PAST_DUE_THRESHOLD_HOURS,
  SUSPECT_THRESHOLD_BUSINESS_HOURS,
  SUSPECT_THRESHOLD_DAYS,
  applyPastDueFilters,
  buildACFilterOptions,
  buildACLeadStats,
  buildACSummaries,
  formatOverdueDuration,
  formatDashboardCount,
  formatThresholdLabel,
} from './utils/acLeadUtils';
import {
  Container,
  FreshnessNote,
  StatCard,
  StatLabel,
  StatSub,
  StatValue,
  StatsRow,
  ThresholdFilter,
  ThresholdLabel,
  ThresholdRow,
} from './styles';
import texts from './texts.json';

/** The fields the toolbar search matches, over the flat queue entries the rows are grouped from. */
const SEARCH_COLUMNS = [
  { field: 'acName', quickSearch: true },
  { field: 'coachName', quickSearch: true },
  { field: 'podName', quickSearch: true },
];

/** Zero stands for "anything that has breached", so it reads as the unfiltered option. */
const renderThresholdLabel = (hours) => (hours === 0 ? texts.threshold.any : formatThresholdLabel(hours));

/**
 * The suspect switch, with the explanation it needs.
 *
 * "Hide suspect (>30d)" is the least self-evident control on the page — it changes what the
 * counted totals mean rather than which rows are listed — so the tooltip is part of the control,
 * not decoration. Wrapped in a span because LabelCheckbox holds no ref of its own.
 */
const HideSuspectFilter = ({ description, isChecked, onChange }) => (
  <Tooltip arrow placement="bottom" title={format(texts.suspectFilter.tooltip, { days: SUSPECT_THRESHOLD_DAYS })}>
    <span>
      <LabelCheckbox description={description} isChecked={isChecked} onChange={onChange} />
    </span>
  </Tooltip>
);

HideSuspectFilter.propTypes = {
  description: PropTypes.string.isRequired,
  isChecked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ACLeadDashboard = () => {
  const {
    queueEntries, acAssignments, selectableACNamesById, hasLoadedDecorations, isReady,
  } = useACLeadData();
  const { selectedIds: selectedACIds, setSelectedIds: setSelectedACIds } = useStoredFilter(AC_FILTER_STORAGE_KEY);
  /*
   * The threshold and the suspect switch are single values held in the same array-shaped store as
   * the AC filter, so they inherit its persistence and its no-op guard rather than growing a second
   * mechanism each.
   */
  const {
    selectedIds: storedThreshold,
    setSelectedIds: setStoredThreshold,
  } = useStoredFilter(PAST_DUE_THRESHOLD_STORAGE_KEY);
  const {
    selectedIds: storedHideSuspect,
    setSelectedIds: setStoredHideSuspect,
  } = useStoredFilter(HIDE_SUSPECT_STORAGE_KEY);
  /*
   * Validated against the menu rather than trusted. A stored value that is no longer offered — the
   * 6h option this dashboard used to have, say — would otherwise filter at a threshold no button
   * matches, leaving the toggle group blank while quietly hiding rows; a non-numeric one would give
   * NaN, and every comparison against NaN is false, so the whole roster would read Clear with
   * nothing flagged.
   */
  const storedHours = Number(storedThreshold[0]);
  const thresholdHours = PAST_DUE_THRESHOLD_HOURS.includes(storedHours) ? storedHours : 0;
  const hideSuspect = storedHideSuspect.length > 0;

  const acOptions = useMemo(
    () => buildACFilterOptions(acAssignments, selectableACNamesById),
    [acAssignments, selectableACNamesById],
  );

  // No selection means every AC, so the dashboard opens on the full picture.
  const visibleEntries = useMemo(() => (
    selectedACIds.length > 0 ? queueEntries.filter(({ acId }) => selectedACIds.includes(acId)) : queueEntries
  ), [queueEntries, selectedACIds]);

  /*
   * Search runs over the queue entries and the rows are grouped afterwards, rather than the grid
   * filtering grouped rows. Filtering those directly would strip an AC row while keeping its coach
   * rows, leaving orphaned detail under whoever happened to be above it.
   */
  const { filteredRows: matchingEntries, toolbarProps } = useQuickSearch(visibleEntries, SEARCH_COLUMNS);

  const acSummaries = useMemo(
    () => buildACSummaries(applyPastDueFilters(matchingEntries, { thresholdHours, hideSuspect })),
    [matchingEntries, thresholdHours, hideSuspect],
  );
  // Stats describe what is on screen, so they move with the filters rather than always totalling
  // everyone — otherwise the headline numbers would contradict the list.
  const stats = useMemo(() => buildACLeadStats(acSummaries), [acSummaries]);

  /*
   * The suspect band is reported as a share of everything that has breached rather than of what is
   * in scope, so the tile answers "how much of this backlog do we not trust" with a number the
   * threshold and the suspect switch cannot move — including when the lead has just switched the
   * band itself out of scope, which is when they most want to know how big it is. The AC filter and
   * the search do still narrow it: those choose who the page is about, not what counts as late.
   */
  const suspectShare = stats.breachedCount > 0
    ? Math.round((stats.suspectCount / stats.breachedCount) * 100)
    : 0;

  const statCards = [
    {
      value: formatDashboardCount(stats.acCount),
      label: texts.stats.assistantCoaches,
      sub: stats.coachAssignmentCount === 1
        ? texts.stats.assistantCoachesSubSingular
        : format(texts.stats.assistantCoachesSub, { count: formatDashboardCount(stats.coachAssignmentCount) }),
    },
    {
      value: formatDashboardCount(stats.itemsPastDue),
      label: texts.stats.itemsPastDue,
      /*
       * Both parts are counted out of the headline above them, never out of `breachedCount`: that
       * one deliberately holds excluded breaches too, so subtracting from it would print a split
       * larger than the total it explains the moment a threshold was set. Splitting the number the
       * card actually shows is the whole point — the old stacked cell put pending counts beside a
       * past-due total, and that is the arithmetic this dashboard is meant to have stopped failing.
       */
      sub: hideSuspect
        ? format(texts.stats.itemsPastDueHiddenSub, { suspect: formatDashboardCount(stats.suspectCount) })
        : format(texts.stats.itemsPastDueSub, {
          actionable: formatDashboardCount(stats.itemsPastDue - stats.suspectCount),
          suspect: formatDashboardCount(stats.suspectCount),
        }),
      isAlert: stats.itemsPastDue > 0,
    },
    {
      value: formatDashboardCount(stats.acsNeedingAttention),
      label: texts.stats.acsNeedAttention,
      sub: format(texts.stats.acsNeedAttentionSub, { count: stats.acsNeedingAttention, total: stats.acCount }),
      isAlert: stats.acsNeedingAttention > 0,
    },
    {
      /*
       * How far past SLA the worst wait is, which is what the label promises — rather than the
       * item's age. Rendered the same way as the Overdue column so the headline and the row it
       * comes from read as one number, with the total age underneath rather than in its place.
       */
      value: stats.hasWorstOverdue ? formatOverdueDuration(stats.worstOverdueHours) : texts.stats.noneValue,
      label: texts.stats.worstWait,
      sub: stats.worstOverdueSince
        ? format(texts.stats.worstWaitSub, { age: formatRelativeTime(moment.unix(stats.worstOverdueSince.seconds)) })
        : texts.stats.worstWaitNoneSub,
    },
    {
      value: formatDashboardCount(stats.suspectCount),
      label: format(texts.stats.suspect, { days: SUSPECT_THRESHOLD_DAYS }),
      sub: stats.suspectCount > 0
        ? format(texts.stats.suspectSub, { share: suspectShare })
        : texts.stats.suspectNoneSub,
      isMuted: true,
    },
  ];

  // Stored as a string because the filter store only keeps strings, and read back as hours above.
  const handleThresholdSelected = useCallback((hours) => (
    setStoredThreshold(hours > 0 ? [String(hours)] : [])
  ), [setStoredThreshold]);

  // A one-value store, so the switch is "has an entry" rather than a boolean of its own.
  const handleHideSuspectChange = useCallback((isHidden) => (
    setStoredHideSuspect(isHidden ? [String(SUSPECT_THRESHOLD_BUSINESS_HOURS)] : [])
  ), [setStoredHideSuspect]);

  /*
   * The AC filter mounts only once its options are the ones it will keep — both loaded and settled.
   * MultiSelectFilter resolves its initial values against whatever list it first sees and never
   * revisits them, so mounting early is not merely a flash: a stored selection would be dropped
   * against an empty list, or resolved against the unnarrowed fallback the options fall back to
   * while the role read is still in flight. The latter is the worse of the two — the resolved
   * option objects are then replaced wholesale when the narrowed list arrives, and MUI's
   * multi-select compares them by reference, so the selection stops matching its own menu entry and
   * clicking it re-adds it rather than clearing it.
   *
   * `onValuesSelected` must stay referentially stable: MultiSelectFilter lists that callback in the
   * deps of the effect that invokes it, so a handler rebuilt on every selection would re-fire the
   * effect that changed it and loop.
   *
   * The suspect switch has no such dependency, so it is offered from the first render.
   */
  const filterTools = useMemo(() => [
    {
      Component: HideSuspectFilter,
      id: 'hide-suspect',
      props: {
        description: format(texts.suspectFilter.label, { days: SUSPECT_THRESHOLD_DAYS }),
        isChecked: hideSuspect,
        onChange: handleHideSuspectChange,
      },
    },
    ...(hasLoadedDecorations && acOptions.length > 0
      ? [{
        Component: MultiSelectFilter,
        id: 'ac-filter',
        props: {
          description: texts.acFilter,
          options: acOptions,
          initialValues: selectedACIds,
          onValuesSelected: setSelectedACIds,
        },
      }]
      : []),
  ], [
    acOptions,
    hasLoadedDecorations,
    selectedACIds,
    setSelectedACIds,
    hideSuspect,
    handleHideSuspectChange,
  ]);

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subTitle}</SubTitle>
        </TitleContainer>
        <ThresholdFilter>
          {/* Labelled, because `Any | 12h | 1d` alone never says what the hours are counted from
              — and "past due by more than" is the only reading that makes `Any` sensible. */}
          <ThresholdRow>
            <ThresholdLabel>{texts.threshold.label}</ThresholdLabel>
            <StyledToggleButtonGroup
              value={thresholdHours}
              exclusive
              // Ignore a null value so clicking the selected option cannot clear the filter entirely.
              onChange={(event, hours) => { if (hours !== null) handleThresholdSelected(hours); }}
            >
              {PAST_DUE_THRESHOLD_HOURS.map((hours) => (
                <ToggleButton value={hours} key={hours}>
                  {renderThresholdLabel(hours)}
                </ToggleButton>
              ))}
            </StyledToggleButtonGroup>
          </ThresholdRow>
          <Tooltip arrow placement="bottom-end" title={texts.freshness.tooltip}>
            <FreshnessNote>
              {format(texts.freshness.label, { minutes: COUNTS_RECOMPUTE_INTERVAL_MINUTES })}
            </FreshnessNote>
          </Tooltip>
        </ThresholdFilter>
      </HeaderRow>
      <StatsRow>
        {statCards.map(({
          value, label, sub, isAlert, isMuted,
        }) => (
          <StatCard key={label} $isAlert={isAlert} $isMuted={isMuted}>
            <StatValue $isMuted={isMuted}>{value}</StatValue>
            <StatLabel>{label}</StatLabel>
            <StatSub>{sub}</StatSub>
          </StatCard>
        ))}
      </StatsRow>
      <ACLeadTable
        summaries={acSummaries}
        isLoading={!isReady}
        /*
         * The search narrows the list like the others, so an empty grid after searching reports no
         * results rather than claiming everyone is clear. Trimmed to match useQuickSearch, which
         * trims before matching — whitespace alone filters nothing and so is not a filter.
         */
        hasFilter={selectedACIds.length > 0 || thresholdHours > 0 || hideSuspect || !!toolbarProps.value.trim()}
        toolbarProps={toolbarProps}
        filterTools={filterTools}
      />
    </Container>
  );
};

export default ACLeadDashboard;
